import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import api from "../api";
import DateIT from "../components/DateIT";
import { Modal, KINDS } from "../components/ui";
import EmailComposer from "../components/EmailComposer";
import Contact360 from "../components/Contact360";
import SenderCard from "../components/SenderCard";

const GENERIC_DOMAINS = new Set(["gmail.com", "googlemail.com", "hotmail.com", "hotmail.it", "libero.it", "yahoo.com", "yahoo.it", "outlook.com", "outlook.it", "live.it", "live.com", "icloud.com", "alice.it", "tin.it", "virgilio.it", "tiscali.it", "pec.it", "aruba.it", "pec.aruba.it", "legalmail.it"]);
function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function attMeta(a) {
  const ct = (a.content_type || "").toLowerCase();
  const fn = (a.filename || "").toLowerCase();
  const ext = (fn.split(".").pop() || "").toLowerCase();
  const isImage = ct.startsWith("image") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)$/.test(fn);
  const isPdf = ct.includes("pdf") || ext === "pdf";
  const isAudio = ct.startsWith("audio") || /\.(ogg|oga|opus|mp3|m4a|wav|amr)$/.test(fn);
  let icon = "📄", kind = ext.toUpperCase() || "FILE";
  if (isImage) { icon = "🖼"; kind = "Immagine"; }
  else if (isPdf) { icon = "📕"; kind = "PDF"; }
  else if (isAudio) { icon = "🎵"; kind = "Audio"; }
  else if (/(doc|docx|odt)$/.test(ext)) { icon = "📘"; kind = "Word"; }
  else if (/(xls|xlsx|ods|csv)$/.test(ext)) { icon = "📗"; kind = "Excel"; }
  else if (/(ppt|pptx)$/.test(ext)) { icon = "📙"; kind = "PowerPoint"; }
  else if (/(zip|rar|7z|tar|gz)$/.test(ext)) { icon = "🗜"; kind = "Archivio"; }
  else if (/(xml|p7m)$/.test(ext)) { icon = "🧾"; kind = "Fattura/XML"; }
  return { isImage, isPdf, isAudio, icon, kind };
}

export default function EmailDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [msg, setMsg] = useState(null);
  const [notes, setNotes] = useState("");
  const [tasks, setTasks] = useState([]);
  const [modal, setModal] = useState(null); // "reply" | "forward" | "assign" | "task" | "block"
  const [blockScope, setBlockScope] = useState("domain");

  // assign
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selCompany, setSelCompany] = useState("");
  const [selProject, setSelProject] = useState("");
  const [selFolder, setSelFolder] = useState("");
  const [showClosedF, setShowClosedF] = useState(false);
  const [newProject, setNewProject] = useState("");
  const [newFolder, setNewFolder] = useState("");
  // task
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});
  const [transcribing, setTranscribing] = useState(null);
  const transcribe = async (idx) => {
    setTranscribing(idx);
    try {
      const r = await api.post(`/emails/${id}/attachments/${idx}/transcribe`);
      setAttachments((prev) => prev.map((a) => (a.index === idx ? { ...a, transcription: r.data.transcription } : a)));
    } catch (e) {
      alert("Trascrizione fallita: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTranscribing(null);
    }
  };
  const [waText, setWaText] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [thread, setThread] = useState([]);
  const [openMsgs, setOpenMsgs] = useState({});
  const [linkCompanies, setLinkCompanies] = useState([]);
  const [linkSel, setLinkSel] = useState("");
  const [contact, setContact] = useState(null); // {first_name,last_name,email,phone,kind}
  const [attach, setAttach] = useState(null); // aggiungi mittente ad anagrafica esistente {target,id,q}
  const [attachPersons, setAttachPersons] = useState([]);
  const [attachLeads, setAttachLeads] = useState([]);
  const [extracting, setExtracting] = useState(false); // estrazione AI dati dal corpo email
  const [leadForm, setLeadForm] = useState(null); // crea lead "da valutare" dal messaggio

  const openLead = async () => {
    const isPhone = msg.channel === "whatsapp";
    setLeadForm({
      name: (msg.from_name || "").trim(),
      email: isPhone ? "" : (msg.from_email || ""),
      phone: isPhone ? (msg.from_email || "") : "",
      note: "",
    });
    if (!isPhone) {
      const d = await extractData(true);
      if (d) setLeadForm((f) => f && ({
        ...f,
        name: [d.first_name, d.last_name].filter(Boolean).join(" ") || f.name,
        email: d.email || f.email,
        phone: d.phone || f.phone,
        note: [d.note, d.company ? `Azienda: ${d.company}` : ""].filter(Boolean).join(" · "),
      }));
    }
  };
  const saveLead = async () => {
    try {
      await api.post("/pipeline", {
        name: leadForm.name.trim(), email: leadForm.email.trim() || null,
        phone: leadForm.phone.trim() || null, note: leadForm.note.trim() || null,
        stage: "da_valutare",
      });
      setLeadForm(null);
      alert('Lead creato nella colonna "Da valutare": da lì lo promuovi a Primo contatto oppure lo chiudi.');
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };
  const [evt, setEvt] = useState(null); // creazione evento calendario

  const openAttach = () => {
    setAttach({ target: "person", id: "", q: "" });
    if (attachPersons.length === 0) api.get("/persons").then((r) => setAttachPersons(r.data)).catch(() => {});
    if (companies.length === 0) api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    if (attachLeads.length === 0) api.get("/pipeline?view=all").then((r) => setAttachLeads(Array.isArray(r.data) ? r.data : r.data.leads || [])).catch(() => {});
  };
  const saveAttach = async () => {
    const val = (msg.from_email || "").trim();
    if (!attach.id || !val) return;
    const field = isWa ? "phone" : "email";
    const fieldLabel = isWa ? "il telefono" : "l'email";
    try {
      if (attach.target === "lead") {
        await api.post(`/pipeline/${attach.id}/attach-contact`, { [field]: val });
        const l = attachLeads.find((x) => x.id === attach.id);
        setAttach(null);
        alert(`Collegato al lead "${l?.name || l?.email || ""}": tutti i suoi messaggi (anche futuri) compariranno nella cronologia del lead.`);
        load();
        return;
      }
      if (attach.target === "person") {
        const p = attachPersons.find((x) => x.id === attach.id);
        const cur = (p?.[field] || "").trim();
        if (cur && cur.toLowerCase() !== val.toLowerCase()
          && !window.confirm(`${p.first_name || ""} ${p.last_name || ""} ha già ${fieldLabel} "${cur}".\nLo sostituisco con "${val}"?`)) return;
        await api.put(`/persons/${attach.id}`, { [field]: val });
      } else {
        const c = companies.find((x) => x.id === attach.id);
        const cur = (c?.[field] || "").trim();
        if (cur && cur.toLowerCase() !== val.toLowerCase()
          && !window.confirm(`${c.name} ha già ${fieldLabel} "${cur}".\nLo sostituisco con "${val}"?`)) return;
        await api.put(`/companies/${attach.id}`, { [field]: val });
      }
      setAttach(null);
      alert("Anagrafica aggiornata: il mittente ora è riconosciuto, anche per i messaggi futuri.");
      load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const openContact = () => {
    const nm = (msg.from_name || "").trim();
    const parts = nm.split(" ");
    const isPhone = isWa;
    setContact({
      first_name: parts[0] || (isPhone ? "Contatto" : nm) || "",
      last_name: parts.slice(1).join(" "),
      email: isPhone ? "" : (msg.from_email || ""),
      phone: isPhone ? (msg.from_email || "") : "",
      kind: "lead",
      company_id: "",
      role: "",
      note: "",
      company_name: "",
    });
    if (companies.length === 0) api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    // per le email (es. notifiche dal sito/acquisti) prova a estrarre i dati dal corpo
    if (msg.channel !== "whatsapp") runExtract(true);
  };
  const extractData = async (silent) => {
    setExtracting(true);
    try {
      const r = await api.get(`/emails/${id}/extract-contact`, { noCache: true });
      return r.data;
    } catch (e) {
      if (!silent) alert("Estrazione non riuscita: " + (e?.response?.data?.detail || e.message));
      return null;
    } finally { setExtracting(false); }
  };
  const runExtract = async (silent = false) => {
    const d = await extractData(silent);
    if (!d) return;
    setContact((c) => c && ({
      ...c,
      first_name: d.first_name || c.first_name,
      last_name: d.last_name || c.last_name,
      email: d.email || c.email,
      phone: d.phone || c.phone,
      note: d.note || c.note,
      company_name: d.company || c.company_name,
    }));
  };
  const saveContact = async () => {
    if (!contact.first_name.trim()) { alert("Indica almeno il nome"); return; }
    try {
      const chk = await api.get("/persons/by-contact", { params: { email: contact.email.trim(), phone: contact.phone.trim() }, noCache: true });
      const ex = chk.data?.person;
      if (ex) {
        const exName = `${ex.first_name || ""} ${ex.last_name || ""}`.trim();
        if (!window.confirm(`⚠️ Esiste già "${exName}" in anagrafica con questa email/telefono.\n\nOK = creo comunque un DOPPIONE (sconsigliato)\nAnnulla = non creo nulla (usa la scheda esistente qui sotto)`)) {
          setContact(null);
          return;
        }
      }
    } catch (_) {}
    try {
      const r = await api.post("/persons", {
        first_name: contact.first_name.trim(), last_name: contact.last_name.trim() || "—",
        kind: contact.kind, email: contact.email.trim() || null, phone: contact.phone.trim() || null,
        role: contact.role.trim() || null,
        notes: contact.note ? `Creato da email: ${contact.note}` : "Creato dal mittente di un'email",
      });
      if (contact.company_id) {
        try { await api.post("/persons/links", { person_id: r.data.id, company_id: contact.company_id, role: contact.role.trim() || null }); }
        catch (err) { console.error("Errore collegamento azienda:", err); }
      }
      setContact(null);
      alert("Anagrafica creata" + (contact.company_id ? " e collegata al cliente." : ". Il mittente ora è riconosciuto."));
      load();
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const isWa = msg && msg.channel === "whatsapp";

  const openEvent = () => {
    setEvt({
      title: msg.subject ? `Appuntamento · ${msg.subject}` : `Appuntamento con ${msg.from_name || msg.from_email}`,
      date: "", time: "10:00", dur: 30, target: "crm", invite: false, desc: "",
    });
  };
  const saveEvent = async () => {
    try {
      if (evt.target === "crm" || evt.target === "both") {
        await api.post("/appointments", {
          date: evt.date, time: evt.time, dur: Number(evt.dur) || 30, title: evt.title,
          name: msg.from_name || msg.from_email,
          email: !isWa && msg.from_email ? msg.from_email : null,
          phone: isWa ? msg.from_email : null,
          mode: "presenza", category: "cliente",
        });
      }
      if (evt.target === "google" || evt.target === "both") {
        await api.post("/calendar/events", {
          summary: evt.title,
          description: evt.desc || `Creato dall'email di ${msg.from_email}`,
          start: `${evt.date}T${evt.time}:00`,
          duration_min: Number(evt.dur) || 30,
          attendees: evt.invite && !isWa && msg.from_email ? [msg.from_email] : [],
        });
      }
      setEvt(null);
      alert("Evento creato nel calendario ✔");
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const onFrameLoad = (e) => {
    const f = e.target;
    // i link delle email si aprono sempre in una nuova scheda (molti siti rifiutano di aprirsi nel riquadro)
    try {
      const doc = f.contentDocument || f.contentWindow.document;
      doc.querySelectorAll("a[href]").forEach((a) => { a.target = "_blank"; a.rel = "noopener noreferrer"; });
    } catch (_) {}
    const resize = () => {
      try {
        const doc = f.contentDocument || f.contentWindow.document;
        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        if (h > 40) f.style.height = (h + 28) + "px";
      } catch (_) {}
    };
    resize();
    setTimeout(resize, 350);
    setTimeout(resize, 1200);
  };

  const load = useCallback(() => {
    api.get(`/emails/${id}`).then((r) => {
      setMsg(r.data); setNotes(r.data.notes || "");
      if (r.data.channel === "whatsapp" && r.data.from_email) {
        api.get(`/whatsapp/conversation/${r.data.from_email}`).then((c) => setConversation(c.data)).catch(() => {});
      } else {
        api.get(`/emails/${id}/thread`).then((t) => setThread(t.data.messages || [])).catch(() => setThread([]));
      }
    }).catch(() => setMsg(null));
    api.get(`/tasks?email_id=${id}`).then((r) => setTasks(r.data)).catch(() => {});
    api.get(`/emails/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Anteprime immagini: scaricate via axios autenticato (gli <img src> non inviano il token)
  useEffect(() => {
    const created = [];
    attachments.forEach((a) => {
      if (attMeta(a).isImage) {
        api.get(`/emails/${id}/attachments/${a.index}?inline=1`, { responseType: "blob" })
          .then((r) => {
            const u = URL.createObjectURL(r.data);
            created.push(u);
            setBlobUrls((prev) => ({ ...prev, [a.index]: u }));
          }).catch(() => {});
      }
    });
    return () => { created.forEach((u) => URL.revokeObjectURL(u)); };
  }, [attachments, id]);

  const openAttachment = async (a) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/emails/${id}/attachments/${a.index}?inline=1`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      if (w) w.location = u; else window.open(u, "_blank");
    } catch (e) { if (w) w.close(); alert("Errore apertura allegato: " + (e?.response?.status === 401 ? "sessione scaduta" : e.message)); }
  };
  const downloadBlob = (data, filename) => {
    const u = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = u; link.download = filename || "allegato";
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(u), 2000);
  };
  const downloadAttachment = async (a) => {
    try {
      const r = await api.get(`/emails/${id}/attachments/${a.index}`, { responseType: "blob" });
      downloadBlob(r.data, a.filename);
    } catch (e) { alert("Errore download allegato: " + e.message); }
  };
  const downloadZip = async () => {
    try {
      const r = await api.get(`/emails/${id}/attachments-zip`, { responseType: "blob" });
      downloadBlob(r.data, `allegati_${(msg.subject || "email").slice(0, 30)}.zip`);
    } catch (e) { alert("Errore download zip: " + e.message); }
  };
  const savePdf = () => {
    const w = window.open("", "_blank");
    if (!w) { alert("Abilita i popup per salvare in PDF."); return; }
    const content = msg.body_html || `<pre style="white-space:pre-wrap;font-family:Arial,sans-serif">${(msg.body_text || "").replace(/</g, "&lt;")}</pre>`;
    w.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${(msg.subject || "Email").replace(/</g, "")}</title></head>
      <body style="margin:24px;font-family:Arial,sans-serif;color:#111">
        <h2 style="margin:0 0 4px">${(msg.subject || "(senza oggetto)").replace(/</g, "&lt;")}</h2>
        <p style="color:#555;margin:0 0 12px">Da: ${(msg.from_name || "").replace(/</g, "&lt;")} &lt;${msg.from_email || ""}&gt;${msg.date ? " · " + new Date(msg.date).toLocaleString("it-IT") : ""}</p>
        <hr style="border:none;border-top:1px solid #ddd"/>${content}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 500);
  };

  const sendWaReply = async () => {
    if (waBusy || !waText.trim()) return;
    setWaBusy(true);
    try {
      await api.post("/whatsapp/reply", { to: msg.from_email, body: waText, reply_to_id: id });
      setWaText(""); setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setWaBusy(false); }
  };
  const saveWaDraft = async () => {
    if (waBusy || !waText.trim()) return;
    setWaBusy(true);
    try {
      await api.post("/emails/drafts", {
        channel: "whatsapp", to: msg.from_email, wa_name: msg.from_name || "",
        body_text: waText.trim(), reply_to_id: id,
        quoted_text: msg.body_text || "", quoted_at: msg.date || "",
      });
      setModal(null); setWaText("");
      alert("Bozza WhatsApp salvata: la ritrovi in Da gestire → 📝 Bozze.");
    } catch (e) { alert(e.response?.data?.detail || e.message); }
    finally { setWaBusy(false); }
  };
  const openLink = () => {
    setModal("link"); setLinkSel("");
    api.get("/companies").then((r) => setLinkCompanies(r.data));
  };
  const doLink = async () => {
    if (!linkSel) return;
    try {
      const r = await api.post("/whatsapp/link-number", { phone: msg.from_email, company_id: linkSel });
      setModal(null);
      alert(`Numero collegato. ${r.data.messages_updated} messaggi attribuiti, ${r.data.contacts_merged} contatti uniti.`);
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const toggleStar = async () => {
    await api.post(`/emails/${id}/star`, { starred: !msg.starred }); load();
  };

  const saveNotes = async () => {
    await api.put(`/emails/${id}/notes`, { notes });
    alert("Appunti salvati");
  };

  const openAssign = () => {
    setModal("assign");
    setNewProject(""); setNewFolder("");
    api.get("/companies").then((r) => setCompanies(r.data));
  };
  const onCompany = (cid) => {
    setSelCompany(cid); setSelProject(""); setSelFolder(""); setFolders([]); setNewProject(""); setNewFolder("");
    api.get(`/projects?company_id=${cid}`).then((r) => setProjects(r.data));
  };
  const onProject = (pid) => {
    setSelProject(pid); setSelFolder(""); setNewFolder("");
    api.get(`/projects/${pid}`).then((r) => setFolders(r.data.folders));
  };
  const createProject = async () => {
    if (!newProject.trim() || !selCompany) return;
    try {
      const r = await api.post("/projects", { company_id: selCompany, name: newProject.trim() });
      setNewProject("");
      const pr = await api.get(`/projects?company_id=${selCompany}`); setProjects(pr.data);
      onProject(r.data.id);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const createFolder = async () => {
    if (!newFolder.trim() || !selProject) return;
    try {
      const r = await api.post("/projects/folders", { project_id: selProject, name: newFolder.trim() });
      setNewFolder("");
      const pr = await api.get(`/projects/${selProject}`); setFolders(pr.data.folders);
      setSelFolder(r.data.id);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const blockSender = () => setModal("block");

  const markHandled = async () => {
    try { await api.post(`/emails/${id}/handle`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const reopenEmail = async () => {
    try { await api.post(`/emails/${id}/reopen`); load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const deleteEmail = async () => {
    if (!window.confirm("Eliminare definitivamente questo messaggio dal CRM?")) return;
    try { await api.delete(`/emails/${id}`); navigate("/da-gestire"); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const confirmBlock = async () => {
    const domain = (msg.from_email || "").split("@")[1] || "";
    const useDomain = blockScope === "domain" && domain && !GENERIC_DOMAINS.has(domain.toLowerCase());
    try {
      const r = await api.post("/exclusion-rules", {
        value: useDomain ? domain : msg.from_email,
        match_type: useDomain ? "domain" : "email",
      });
      setModal(null);
      navigate("/da-gestire", { state: { blocked: useDomain ? "@" + domain : msg.from_email, deleted: r.data.emails_eliminate } });
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const doAssign = async () => {
    try {
      await api.post(`/emails/${id}/assign`, { folder_id: selFolder });
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const doTask = async () => {
    try {
      await api.post("/tasks", { title: taskTitle, due_date: taskDue || null, email_id: id, company_id: msg.company_id || null });
      setModal(null); setTaskTitle(""); setTaskDue(""); load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const toggleTask = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: t.status === "fatto" ? "da_fare" : "fatto" });
    load();
  };

  if (!msg) return <p className="muted">Caricamento…</p>;

  return (
    <div>
      <p><Link to={location.state?.backTo || "/da-gestire"}>← {location.state?.backLabel || "Da gestire"}</Link></p>
      <h1 className="page-title">{msg.subject || "(senza oggetto)"}</h1>
      <p className="page-sub">
        Da <strong>{msg.from_name || msg.from_email}</strong> &lt;{msg.from_email}&gt; ·{" "}
        <span className={`pill ${msg.status}`}>{msg.status === "da_gestire" ? "Da gestire" : `Gestita${msg.handled_reason ? " · " + msg.handled_reason : ""}`}</span>
      </p>

      <div className="toolbar">
        <button className="btn primary" onClick={() => setModal(isWa ? "wareply" : "reply")} data-testid="reply-btn">{isWa ? "↩ Rispondi su WhatsApp" : "Rispondi"}</button>
        {!isWa && <button className="btn" onClick={() => setModal("replyall")} data-testid="reply-all-btn">↩↩ Rispondi a tutti</button>}
        {!isWa && <button className="btn" onClick={() => setModal("forward")} data-testid="forward-btn">Inoltra</button>}
        {!isWa && <button className="btn" onClick={savePdf} data-testid="save-pdf-btn">🖨 Salva PDF</button>}
        <button className="btn" onClick={toggleStar} data-testid="star-btn">{msg.starred ? "★ Importante" : "☆ Importante"}</button>
        <button className="btn" onClick={openAssign} data-testid="assign-btn">Abbina a cartella</button>
        {isWa && !msg.is_client && <button className="btn" onClick={openLink} data-testid="link-client-btn">🔗 Collega a cliente</button>}
        <button className="btn" onClick={openContact} data-testid="create-contact-btn">➕ Crea anagrafica</button>
        <button className="btn" onClick={openLead} data-testid="create-lead-btn" title='Crea un lead nella colonna "Da valutare" del funnel: tutta la corrispondenza verrà raggruppata lì'>⭐ Crea lead</button>
        <button className="btn" onClick={openAttach} data-testid="attach-contact-btn" title="Salva il mittente su una persona o azienda già presente in anagrafica">🔗 Aggiungi ad anagrafica</button>
        <button className="btn" onClick={() => setModal("task")} data-testid="email-task-btn">+ Incarico</button>
        <button className="btn" onClick={openEvent} data-testid="email-event-btn">📅 + Evento</button>
        {msg.status === "da_gestire"
          ? <button className="btn" onClick={markHandled} data-testid="mark-handled-btn">Segna gestita</button>
          : <button className="btn" onClick={reopenEmail} data-testid="reopen-btn">Riporta in Da gestire</button>}
        <div className="spacer" />
        <button className="btn danger" onClick={deleteEmail} data-testid="delete-email-btn">Elimina</button>
        {!isWa && <button className="btn danger" onClick={blockSender} data-testid="block-sender-btn">Blocca mittente</button>}
      </div>

      <SenderCard email={!isWa ? msg.from_email : ""} phone={isWa ? msg.from_email : ""} senderName={msg.from_name} onChanged={load} />
      <Contact360 email={!isWa ? msg.from_email : ""} phone={isWa ? msg.from_email : ""} name={msg.from_name} />

      <div className="panel">
        <h3>Messaggio</h3>
        <div className="email-meta" data-testid="email-meta" style={{ borderBottom: "1px solid var(--border)", paddingBottom: 12, marginBottom: 12, fontSize: 13, lineHeight: 1.7 }}>
          <div data-testid="email-meta-from"><strong>Da:</strong> {msg.from_name ? `${msg.from_name} ` : ""}&lt;{msg.from_email}&gt;</div>
          {msg.to?.length > 0 && <div data-testid="email-meta-to"><strong>A:</strong> {msg.to.join(", ")}</div>}
          {msg.cc?.length > 0 && <div data-testid="email-meta-cc"><strong>Cc:</strong> {msg.cc.join(", ")}</div>}
          {msg.account && <div data-testid="email-meta-account"><strong>Casella:</strong> {msg.account}</div>}
          {msg.received_at && <div data-testid="email-meta-date"><strong>Data:</strong> {new Date(msg.received_at).toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" })}</div>}
          {msg.subject && <div data-testid="email-meta-subject"><strong>Oggetto:</strong> {msg.subject}</div>}
        </div>
        {attachments.length > 0 && (
          <div className="att-bar" data-testid="attachments-top-bar">
            <strong style={{ fontSize: 13 }}>📎 {attachments.length} allegat{attachments.length === 1 ? "o" : "i"}:</strong>
            {attachments.map((a) => (
              <span key={a.index} className="att-chip" data-testid={`att-chip-${a.index}`}>
                <span className="att-chip-icon">{attMeta(a).icon}</span>
                <button className="att-chip-name" onClick={() => openAttachment(a)} title={`Apri ${a.filename}`} data-testid={`att-chip-open-${a.index}`}>{a.filename}</button>
                <span className="muted" style={{ fontSize: 11 }}>{fmtSize(a.size)}</span>
                <button className="btn sm" onClick={() => downloadAttachment(a)} title="Scarica" data-testid={`att-chip-dl-${a.index}`}>⬇</button>
              </span>
            ))}
            {attachments.length > 1 && (
              <button className="btn sm primary" onClick={downloadZip} data-testid="att-bar-zip">⬇ Scarica tutti (zip)</button>
            )}
          </div>
        )}
        {msg.body_html ? (
          <iframe
            title="email-html"
            className="email-html-frame"
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            srcDoc={msg.body_html}
            onLoad={onFrameLoad}
            data-testid="email-html-body"
          />
        ) : (
          <div className="email-body" style={{ whiteSpace: "pre-wrap" }}>{msg.body_text || "(nessun testo)"}</div>
        )}
        {msg.attachments?.length > 0 && attachments.length === 0 && (
          <p style={{ marginTop: 12 }}><strong>Allegati:</strong> {msg.attachments.map((a) => a.filename).join(", ")}</p>
        )}
      </div>

      {thread.length > 1 && (
        <div className="panel" data-testid="email-thread-panel">
          <h3>Conversazione ({thread.length} messaggi)</h3>
          <p className="muted" style={{ fontSize: 12, marginTop: -6 }}>Tutta la cronologia di questa email, dal più vecchio al più recente.</p>
          {thread.map((m, i) => {
            const open = openMsgs[i] ?? m.is_current;
            return (
              <div key={m.gmail_id || i} data-testid={`thread-msg-${i}`} style={{ border: "1px solid var(--border)", borderRadius: 8, marginBottom: 8, background: m.is_current ? "rgba(99,102,241,0.06)" : "transparent" }}>
                <button
                  onClick={() => setOpenMsgs((s) => ({ ...s, [i]: !open }))}
                  data-testid={`thread-toggle-${i}`}
                  style={{ width: "100%", textAlign: "left", background: "none", border: "none", padding: "10px 12px", cursor: "pointer", display: "flex", justifyContent: "space-between", gap: 8 }}
                >
                  <span style={{ fontSize: 13 }}>
                    <strong>{m.from_name || m.from_email}</strong>
                    {m.is_current && <span className="muted"> · (questo messaggio)</span>}
                    <br /><span className="muted" style={{ fontSize: 12 }}>{m.snippet}</span>
                  </span>
                  <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {m.received_at ? new Date(m.received_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""} {open ? "▲" : "▼"}
                  </span>
                </button>
                {open && (
                  <div style={{ padding: "0 12px 12px" }}>
                    <div className="email-meta" style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
                      Da: {m.from_email} · A: {(m.to || []).join(", ")}
                    </div>
                    {m.body_html ? (
                      <iframe title={`thread-${i}`} className="email-html-frame" sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" srcDoc={m.body_html} onLoad={onFrameLoad} style={{ width: "100%", minHeight: 160, border: "none" }} />
                    ) : (
                      <div className="email-body" style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>{m.body_text || m.snippet || "(nessun testo)"}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}


      {attachments.length > 0 && (
        <div className="panel" data-testid="attachments-panel">
          <div className="toolbar">
            <h3 style={{ margin: 0 }}>Allegati ({attachments.length})</h3>
            <div className="spacer" />
            <a className="btn primary sm" onClick={downloadZip} style={{ cursor: "pointer" }} data-testid="download-all-zip">⬇ Scarica tutti (zip)</a>
          </div>
          <div className="att-grid">
            {attachments.map((a) => {
              const m = attMeta(a);
              return (
                <div key={a.index} className="att-card" data-testid={`attachment-row-${a.index}`}>
                  <div className="att-thumb" onClick={() => openAttachment(a)} style={{ cursor: "pointer" }} data-testid={`open-attachment-${a.index}`}>
                    {m.isImage && blobUrls[a.index]
                      ? <img src={blobUrls[a.index]} alt={a.filename} loading="lazy" />
                      : <span className="att-icon">{m.icon}</span>}
                  </div>
                  <div className="att-info">
                    <div className="att-name" title={a.filename}>{a.filename}</div>
                    <div className="att-meta">{m.kind} · {fmtSize(a.size)}</div>
                    {a.transcription && (
                      <div className="att-transc" data-testid={`transcription-${a.index}`}>“{a.transcription}”</div>
                    )}
                    <div className="att-actions">
                      <button className="btn sm" onClick={() => openAttachment(a)}>Anteprima</button>
                      <button className="btn sm" onClick={() => downloadAttachment(a)} data-testid={`download-attachment-${a.index}`}>Scarica</button>
                      {m.isAudio && !a.transcription && (
                        <button className="btn sm" onClick={() => transcribe(a.index)} disabled={transcribing === a.index} data-testid={`transcribe-attachment-${a.index}`}>
                          {transcribing === a.index ? "Trascrivo…" : "🎤 Trascrivi"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isWa && conversation.length > 0 && (
        <div className="panel" data-testid="wa-conversation">
          <h3>🗨️ Conversazione WhatsApp</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {conversation.map((m, i) => (
              <div key={i} style={{ alignSelf: m.direction === "out" ? "flex-end" : "flex-start", maxWidth: "75%",
                background: m.direction === "out" ? "rgba(37,211,102,.18)" : "var(--panel-2, rgba(148,163,184,.12))",
                border: "1px solid var(--border)", borderRadius: 12, padding: "8px 12px" }} data-testid={`wa-msg-${i}`}>
                <div style={{ fontSize: 11, opacity: .7, marginBottom: 2 }}>{m.direction === "out" ? "Tu" : (m.name || "Cliente")} · {String(m.at).slice(0, 16)}</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.body || "(nessun testo)"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel">
        <h3>Appunti</h3>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scrivi qui le tue note su questa mail…" />
        <div style={{ marginTop: 10 }}><button className="btn primary" onClick={saveNotes}>Salva appunti</button></div>
      </div>

      <div className="panel">
        <h3>Incarichi collegati</h3>
        <table>
          <thead><tr><th></th><th>Incarico</th><th>Scadenza</th><th>Stato</th></tr></thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td><input type="checkbox" style={{ width: "auto" }} checked={t.status === "fatto"} onChange={() => toggleTask(t)} /></td>
                <td style={{ textDecoration: t.status === "fatto" ? "line-through" : "none" }}>{t.title}</td>
                <td>{t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : "—"}</td>
                <td>{t.status === "fatto" ? "Fatto" : "Da fare"}</td>
              </tr>
            ))}
            {tasks.length === 0 && <tr><td colSpan={4} className="muted">Nessun incarico.</td></tr>}
          </tbody>
        </table>
      </div>

      {evt && (
        <Modal title="Crea evento in calendario" onClose={() => setEvt(null)}>
          <div className="field"><label>Titolo *</label><input value={evt.title} onChange={(e) => setEvt({ ...evt, title: e.target.value })} data-testid="event-title" /></div>
          <div className="grid-2">
            <div className="field"><label>Data *</label><input type="date" value={evt.date} onChange={(e) => setEvt({ ...evt, date: e.target.value })} data-testid="event-date" /></div>
            <div className="field"><label>Ora *</label><input type="time" value={evt.time} onChange={(e) => setEvt({ ...evt, time: e.target.value })} data-testid="event-time" /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Durata (minuti)</label><input type="number" min="10" step="5" value={evt.dur} onChange={(e) => setEvt({ ...evt, dur: e.target.value })} data-testid="event-dur" /></div>
            <div className="field"><label>Dove crearlo</label>
              <select value={evt.target} onChange={(e) => setEvt({ ...evt, target: e.target.value })} data-testid="event-target">
                <option value="crm">Calendario CRM (appuntamento)</option>
                <option value="google">Google Calendar</option>
                <option value="both">Entrambi</option>
              </select>
            </div>
          </div>
          {(evt.target === "google" || evt.target === "both") && !isWa && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={evt.invite} onChange={(e) => setEvt({ ...evt, invite: e.target.checked })} data-testid="event-invite" />
              Invita {msg.from_email} (riceve l'invito da Google Calendar)
            </label>
          )}
          <div className="field"><label>Note</label><textarea rows={2} value={evt.desc} onChange={(e) => setEvt({ ...evt, desc: e.target.value })} data-testid="event-desc" /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setEvt(null)}>Annulla</button>
            <button className="btn primary" onClick={saveEvent} disabled={!evt.title.trim() || !evt.date || !evt.time} data-testid="event-save">Crea evento</button>
          </div>
        </Modal>
      )}


      {(modal === "reply" || modal === "replyall" || modal === "forward") && (
        <EmailComposer
          mode={modal}
          original={msg}
          onClose={() => setModal(null)}
          onSent={load}
        />
      )}

      {modal === "wareply" && (
        <Modal title="Rispondi su WhatsApp" onClose={() => setModal(null)}>
          <p className="muted">A: <strong>{msg.from_name || msg.from_email}</strong> ({msg.from_email})</p>
          <div className="wa-quoted" data-testid="wa-reply-quoted">
            <div className="muted" style={{ fontSize: 11.5, marginBottom: 4 }}>↩︎ Messaggio a cui rispondi{msg.date ? ` · ${new Date(msg.date).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : ""}</div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13, maxHeight: 160, overflowY: "auto" }}>{msg.body_text || "(messaggio senza testo)"}</div>
          </div>
          <textarea rows={4} value={waText} onChange={(e) => setWaText(e.target.value)} style={{ marginTop: 10 }} placeholder="Scrivi la risposta WhatsApp…" data-testid="wa-reply-text" />
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn" onClick={saveWaDraft} disabled={waBusy || !waText.trim()} data-testid="wa-reply-draft">{waBusy ? "…" : "💾 Salva bozza"}</button>
            <button className="btn primary" onClick={sendWaReply} disabled={waBusy || !waText.trim()} data-testid="wa-reply-send">{waBusy ? "Invio…" : "Invia su WhatsApp"}</button>
          </div>
        </Modal>
      )}

      {modal === "link" && (
        <Modal title="Collega numero a un cliente" onClose={() => setModal(null)}>
          <p className="muted">Collego <strong>{msg.from_email}</strong> a un cliente: i messaggi (anche futuri) verranno riconosciuti e il contatto unito all'anagrafica.</p>
          <div className="field"><label>Cliente</label>
            <select value={linkSel} onChange={(e) => setLinkSel(e.target.value)} data-testid="link-company-select">
              <option value="">— Seleziona —</option>
              {linkCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={doLink} disabled={!linkSel} data-testid="link-confirm-btn">Collega</button>
          </div>
        </Modal>
      )}

      {modal === "block" && (() => {
        const domain = (msg.from_email || "").split("@")[1] || "";
        const isGeneric = GENERIC_DOMAINS.has(domain.toLowerCase());
        return (
        <Modal title="Blocca ed elimina" onClose={() => setModal(null)}>
          <p>Non vuoi più ricevere messaggi da <strong>{msg.from_email}</strong>?</p>
          <div className="field" style={{ marginTop: 4 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: isGeneric ? "not-allowed" : "pointer", opacity: isGeneric ? .5 : 1 }}>
              <input type="radio" name="block-scope" checked={blockScope === "domain" && !isGeneric} disabled={isGeneric} onChange={() => setBlockScope("domain")} data-testid="block-scope-domain" />
              <span>Blocca <strong>tutto il dominio @{domain || "—"}</strong> <span className="muted">(consigliato per pubblicità)</span></span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", marginTop: 6 }}>
              <input type="radio" name="block-scope" checked={blockScope === "email" || isGeneric} onChange={() => setBlockScope("email")} data-testid="block-scope-email" />
              <span>Blocca <strong>solo questo indirizzo</strong></span>
            </label>
            {isGeneric && <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>⚠️ @{domain} è un dominio generico (caselle personali): si può bloccare solo il singolo indirizzo, non l'intero dominio.</p>}
          </div>
          <p className="muted" style={{ fontSize: 13 }}>Verranno <strong>eliminate dal CRM tutte le email passate</strong> {blockScope === "domain" && !isGeneric ? "di questo dominio" : "di questo mittente"} e quelle future saranno bloccate automaticamente.</p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)} data-testid="block-cancel-btn">Annulla</button>
            <button className="btn danger" onClick={confirmBlock} data-testid="block-confirm-btn">Sì, blocca ed elimina</button>
          </div>
        </Modal>
        );
      })()}

      {modal === "assign" && (
        <Modal title="Abbina a una cartella" onClose={() => setModal(null)}>
          <div className="field"><label>Azienda</label>
            <select value={selCompany} onChange={(e) => onCompany(e.target.value)}>
              <option value="">— seleziona —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selCompany && (
            <div className="field"><label>Progetto</label>
              <select value={selProject} onChange={(e) => onProject(e.target.value)}>
                <option value="">— seleziona —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <div className="toolbar" style={{ gap: 6, marginTop: 6 }}>
                <input style={{ maxWidth: 220 }} placeholder="Nuovo progetto…" value={newProject} onChange={(e) => setNewProject(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createProject()} data-testid="new-project-inline" />
                <button className="btn sm" onClick={createProject} disabled={!newProject.trim()} data-testid="create-project-inline">+ Crea progetto</button>
              </div>
            </div>
          )}
          {selProject && (
            <div className="field"><label>Cartella</label>
              <select value={selFolder} onChange={(e) => setSelFolder(e.target.value)}>
                <option value="">— seleziona —</option>
                {folders.filter((f) => showClosedF || !f.closed).map((f) => <option key={f.id} value={f.id}>{f.closed ? `🔒 ${f.name} (chiusa)` : f.name}</option>)}
              </select>
              {folders.some((f) => f.closed) && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={showClosedF} onChange={(e) => setShowClosedF(e.target.checked)} data-testid="assign-show-closed" />
                  <span className="muted">Mostra anche le cartelle chiuse ({folders.filter((f) => f.closed).length})</span>
                </label>
              )}
              <div className="toolbar" style={{ gap: 6, marginTop: 6 }}>
                <input style={{ maxWidth: 220 }} placeholder="Nuova cartella… (es. Bilancio 2026)" value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} data-testid="new-folder-inline" />
                <button className="btn sm" onClick={createFolder} disabled={!newFolder.trim()} data-testid="create-folder-inline">+ Crea cartella</button>
              </div>
            </div>
          )}
          <p className="muted" style={{ fontSize: 12 }}>Gli allegati PDF finiranno nell'archivio del cliente. La mail diventa "Gestita".</p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={doAssign} disabled={!selFolder}>Abbina</button>
          </div>
        </Modal>
      )}

      {modal === "task" && (
        <Modal title="Nuovo incarico" onClose={() => setModal(null)}>
          <div className="field"><label>Descrizione *</label><input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="es. Richiamare il cliente" /></div>
          <div className="field"><label>Scadenza</label><DateIT value={taskDue} onChange={(v) => setTaskDue(v)} /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn primary" onClick={doTask} disabled={!taskTitle}>Crea incarico</button>
          </div>
        </Modal>
      )}
      {attach && (
        <Modal title="Aggiungi ad anagrafica esistente" onClose={() => setAttach(null)}>
          <p className="muted">{isWa ? "Il numero" : "L'indirizzo"} <strong>{msg.from_email}</strong> verrà salvato sulla scheda scelta: i messaggi (anche futuri) verranno riconosciuti e raggruppati su quella anagrafica.</p>
          <div className="field"><label>Tipo di anagrafica</label>
            <div style={{ display: "flex", gap: 18 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" name="attach-target" checked={attach.target === "person"} onChange={() => setAttach({ ...attach, target: "person", id: "" })} data-testid="attach-target-person" /> Persona
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" name="attach-target" checked={attach.target === "company"} onChange={() => setAttach({ ...attach, target: "company", id: "" })} data-testid="attach-target-company" /> Azienda
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="radio" name="attach-target" checked={attach.target === "lead"} onChange={() => setAttach({ ...attach, target: "lead", id: "" })} data-testid="attach-target-lead" /> 🎯 Lead
              </label>
            </div>
          </div>
          <div className="field"><label>Cerca</label>
            <input value={attach.q} onChange={(e) => setAttach({ ...attach, q: e.target.value })} placeholder="Filtra per nome…" data-testid="attach-search" />
          </div>
          <div className="field"><label>{attach.target === "person" ? "Persona" : attach.target === "company" ? "Azienda" : "Lead"}</label>
            <select value={attach.id} onChange={(e) => setAttach({ ...attach, id: e.target.value })} size={8} style={{ width: "100%" }} data-testid="attach-select">
              {attach.target === "person"
                ? attachPersons
                    .filter((p) => `${p.last_name || ""} ${p.first_name || ""}`.toLowerCase().includes(attach.q.toLowerCase()))
                    .map((p) => <option key={p.id} value={p.id}>{p.last_name} {p.first_name}{p.email ? ` · ${p.email}` : ""}{p.phone ? ` · ${p.phone}` : ""}</option>)
                : attach.target === "company"
                ? companies
                    .filter((c) => (c.name || "").toLowerCase().includes(attach.q.toLowerCase()))
                    .map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}</option>)
                : attachLeads
                    .filter((l) => `${l.name || ""} ${l.email || ""}`.toLowerCase().includes(attach.q.toLowerCase()))
                    .map((l) => <option key={l.id} value={l.id}>{l.name || "(senza nome)"}{l.email ? ` · ${l.email}` : ""}{l.phone ? ` · ${l.phone}` : ""}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setAttach(null)}>Annulla</button>
            <button className="btn primary" onClick={saveAttach} disabled={!attach.id} data-testid="attach-save">Aggiungi all'anagrafica</button>
          </div>
        </Modal>
      )}
      {leadForm && (
        <Modal title="Crea lead (Da valutare)" onClose={() => setLeadForm(null)}>
          <p className="muted" style={{ fontSize: 12.5 }}>Il lead verrà creato nella colonna <strong>"Da valutare"</strong> del funnel: tutta la sua corrispondenza (email e WhatsApp) sarà raggruppata nella sua cronologia. Poi lo promuovi a "Primo contatto" oppure lo chiudi.</p>
          {extracting && <p className="muted" data-testid="lead-extracting">🤖 Sto leggendo la email per estrarre i dati…</p>}
          <div className="grid-2">
            <div className="field"><label>Nome *</label><input value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} data-testid="lead-name" /></div>
            <div className="field"><label>Email</label><input value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} data-testid="lead-email" /></div>
            <div className="field"><label>Telefono</label><input value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} data-testid="lead-phone" /></div>
            <div className="field"><label>Nota</label><input value={leadForm.note} onChange={(e) => setLeadForm({ ...leadForm, note: e.target.value })} placeholder="es. richiesta dal sito, prodotto acquistato…" data-testid="lead-note" /></div>
          </div>
          <details style={{ marginBottom: 10 }}>
            <summary style={{ cursor: "pointer", fontSize: 13 }} className="muted">📄 Mostra il testo della email (per copiare i dati a mano)</summary>
            <div style={{ maxHeight: 180, overflowY: "auto", whiteSpace: "pre-wrap", fontSize: 12.5, background: "rgba(148,163,184,.08)", borderRadius: 8, padding: 10, marginTop: 6, userSelect: "text" }}>
              {msg.body_text || (msg.body_html ? new DOMParser().parseFromString(msg.body_html, "text/html").body.innerText : "(nessun testo)")}
            </div>
          </details>
          <div className="modal-actions">
            <button className="btn" onClick={() => setLeadForm(null)}>Annulla</button>
            <button className="btn primary" onClick={saveLead} disabled={!leadForm.name.trim() || (!leadForm.email.trim() && !leadForm.phone.trim())} data-testid="lead-save">Crea lead</button>
          </div>
        </Modal>
      )}
      {contact && (
        <Modal title="Crea anagrafica dal mittente" onClose={() => setContact(null)}>
          <div className="toolbar" style={{ marginBottom: 8 }}>
            {extracting
              ? <span className="muted" data-testid="contact-extracting">🤖 Sto leggendo la email per estrarre i dati…</span>
              : <button className="btn sm" onClick={() => runExtract(false)} data-testid="contact-extract-btn" title="Rilegge il corpo della email ed estrae nome, email e telefono del cliente">✨ Compila dai dati nella email</button>}
          </div>
          <div className="grid-2">
            <div className="field"><label>Nome *</label><input value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} data-testid="contact-first" /></div>
            <div className="field"><label>Cognome</label><input value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} data-testid="contact-last" /></div>
            <div className="field"><label>Email</label><input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" /></div>
            <div className="field"><label>Telefono</label><input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="contact-phone" /></div>
          </div>
          <div className="field"><label>Tipo</label>
            <select value={contact.kind} onChange={(e) => setContact({ ...contact, kind: e.target.value })} data-testid="contact-kind">
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </div>
          <div className="grid-2">
            <div className="field"><label>Collega a un cliente <span className="muted">(facoltativo)</span></label>
              <select value={contact.company_id} onChange={(e) => setContact({ ...contact, company_id: e.target.value })} data-testid="contact-company">
                <option value="">— nessuna azienda —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Ruolo</label>
              <input placeholder="es. Consulente paghe, Socio…" value={contact.role} onChange={(e) => setContact({ ...contact, role: e.target.value })} data-testid="contact-role" />
            </div>
          </div>
          {contact.note && <p className="muted" style={{ fontSize: 12.5 }} data-testid="contact-extracted-note">📝 Dalla email: {contact.note}{contact.company_name ? ` · Azienda: ${contact.company_name}` : ""}</p>}
          <details style={{ marginBottom: 10 }} data-testid="contact-email-text">
            <summary style={{ cursor: "pointer", fontSize: 13 }} className="muted">📄 Mostra il testo della email (per copiare i dati a mano)</summary>
            <div style={{ maxHeight: 180, overflowY: "auto", whiteSpace: "pre-wrap", fontSize: 12.5, background: "rgba(148,163,184,.08)", borderRadius: 8, padding: 10, marginTop: 6, userSelect: "text" }}>
              {msg.body_text || (msg.body_html ? new DOMParser().parseFromString(msg.body_html, "text/html").body.innerText : "(nessun testo)")}
            </div>
          </details>
          <div className="modal-actions">
            <button className="btn" onClick={() => setContact(null)}>Annulla</button>
            <button className="btn primary" onClick={saveContact} data-testid="contact-save">Crea anagrafica</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
